import {
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  EmbeddedViewRef,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  TemplateRef,
  ViewContainerRef
} from "@angular/core";
import { AbstractControl, ControlContainer, FormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { StorageService } from "../../routes/services/storage.service";
import { FilterPinRowComponent } from "./filter-pin-row/filter-pin-row";

/**
 * Lugar de la barra principal (`.filter-main`) donde se pintan los filtros que
 * el usuario eligio mostrar. Lleva el registro de los filtros de la pantalla
 * para acomodarlos en el mismo orden que el panel.
 *
 * Uso: `<ng-container appFilterSlot #filterSlot="filterSlot"></ng-container>`
 */
@Directive({
  selector: "[appFilterSlot]",
  exportAs: "filterSlot"
})
export class FilterSlotDirective implements OnInit, OnDestroy {
  /** Filtros registrados de la pantalla, en el orden en que aparecen en el template. */
  items: FilterItemDirective[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    public viewContainer: ViewContainerRef,
    private storageService: StorageService,
    @Optional() private controlContainer: ControlContainer
  ) {}

  /**
   * Reacomoda los filtros cuando cambia la eleccion (incluso desde otra pantalla
   * abierta) y mantiene sincronizadas las dos copias de los filtros visibles.
   * @returns void
   */
  ngOnInit(): void {
    this.storageService
      .watchStorage("visibleFilters")
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.items.forEach((item) => item.render());
      });
    this.syncCopies();
  }

  /**
   * Libera la suscripcion de cambios de preferencia.
   * @returns void
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Un filtro visible tiene dos inputs (barra y panel) sobre el mismo control.
   * En Angular 10 lo que se escribe en uno no se pinta en el otro, asi que al
   * cambiar un control se le reasigna su valor sin emitir para refrescar ambos.
   * @returns void
   */
  private syncCopies(): void {
    const form = this.controlContainer ? this.controlContainer.control : null;
    if (!(form instanceof FormGroup)) return;
    let previous = { ...form.value };
    form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      const changed = Object.keys(value).filter((key) => value[key] !== previous[key]);
      previous = { ...value };
      if (!this.items.some((item) => item.pinnedView)) return;
      changed.forEach((key) => {
        const control: AbstractControl = form.controls[key];
        control.setValue(control.value, { emitEvent: false });
      });
    });
  }

  /**
   * Indica si un filtro se muestra en la barra principal.
   * @param key Llave semantica del filtro.
   * @param fallback Valor si el usuario nunca lo ha elegido.
   * @returns `true` si el filtro va en la barra.
   */
  isVisible(key: string, fallback = false): boolean {
    return this.storageService.isFilterVisible(key, fallback);
  }

  /**
   * Cambia la visibilidad de un filtro; se comparte con las pantallas que usen la misma llave.
   * @param key Llave semantica del filtro.
   * @param visible Nuevo estado del check.
   * @returns void
   */
  setVisible(key: string, visible: boolean): void {
    this.storageService.setFilterVisible(key, visible);
  }

  /**
   * Registra un filtro respetando el orden del template.
   * @param item Filtro a registrar.
   * @returns void
   */
  register(item: FilterItemDirective): void {
    this.items = [...this.items, item].sort((a, b) => a.compareOrder(b));
  }

  /**
   * Quita un filtro del registro (por ejemplo al cambiar de pestaña).
   * @param item Filtro a quitar.
   * @returns void
   */
  unregister(item: FilterItemDirective): void {
    this.items = this.items.filter((current) => current !== item);
  }

  /**
   * Posicion en la barra para un filtro visible, contando solo los visibles previos.
   * @param item Filtro a insertar.
   * @returns Indice dentro del contenedor de la barra.
   */
  indexFor(item: FilterItemDirective): number {
    const before = this.items.slice(0, this.items.indexOf(item));
    return before.filter((current) => current.pinnedView).length;
  }
}

/**
 * Filtro que puede vivir en el panel lateral o en la barra principal segun la
 * preferencia del usuario. Se declara una sola vez, dentro del panel: ahi se
 * pinta como una fila (`app-filter-pin-row`) con el control y su check al
 * lado; si se marca, el control tambien se pinta en la barra. Ambas copias usan
 * el mismo control del `<form [formGroup]>`, asi que funcionan y se mantienen
 * sincronizadas en los dos lugares.
 *
 * Uso:
 *   <div class="flex-item" *appFilterItem="'client'; label: 'filter.client'; slot: filterSlot">
 *   (con `; default: true` se muestra en la barra hasta que el usuario lo desmarque)
 *     ...control...
 *   </div>
 */
@Directive({
  selector: "[appFilterItem]"
})
export class FilterItemDirective implements OnInit, OnDestroy {
  /** Llave semantica compartida entre pantallas ("client", "farm", "pond", "dates"...). */
  @Input("appFilterItem") key = "";

  /** Llave de traduccion del nombre que se muestra en la lista de checks. */
  @Input("appFilterItemLabel") label = "";

  /** Si se muestra en la barra cuando el usuario nunca lo ha elegido (la busqueda va en `true`). */
  @Input("appFilterItemDefault") defaultVisible = false;

  /** Lugar de la barra principal donde se pinta cuando es visible. */
  @Input("appFilterItemSlot") slot: FilterSlotDirective;

  /** Vista pintada en la barra principal, si el filtro es visible. */
  pinnedView: EmbeddedViewRef<any> | null = null;

  private panelRow: ComponentRef<FilterPinRowComponent> | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private template: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private resolver: ComponentFactoryResolver
  ) {}

  /**
   * Se registra en la barra y se pinta donde corresponda.
   * @returns void
   */
  ngOnInit(): void {
    if (!this.slot) {
      this.viewContainer.createEmbeddedView(this.template);
      return;
    }
    this.slot.register(this);
    this.createRow();
    this.render();
  }

  /**
   * Quita el filtro de la barra y del registro.
   * @returns void
   */
  ngOnDestroy(): void {
    if (!this.slot) return;
    this.destroy$.next();
    this.destroy$.complete();
    this.slot.unregister(this);
    this.destroyPinned();
  }

  /**
   * Agrega o quita la copia del filtro en la barra segun la preferencia actual;
   * la fila del panel siempre se conserva.
   * @returns void
   */
  render(): void {
    const visible = this.slot.isVisible(this.key, this.defaultVisible);
    const row = this.panelRow.instance;
    row.checked = visible;
    if (visible && !this.pinnedView) {
      const container = this.slot.viewContainer;
      this.pinnedView = container.createEmbeddedView(this.template, {}, this.slot.indexFor(this));
      // La barra ya se reviso en este ciclo; se pinta de inmediato
      this.pinnedView.detectChanges();
    }
    if (!visible) this.destroyPinned();
  }

  /**
   * Crea la fila del panel (control + check) en el lugar donde se declaro el filtro.
   * @returns void
   */
  private createRow(): void {
    const factory = this.resolver.resolveComponentFactory(FilterPinRowComponent);
    this.panelRow = this.viewContainer.createComponent(factory);
    this.panelRow.instance.label = this.label;
    this.panelRow.instance.template = this.template;
    this.panelRow.instance.changed
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible: boolean) => this.slot.setVisible(this.key, visible));
  }

  /**
   * Orden de aparicion en el template, para acomodar la barra igual que el panel.
   * @param other Filtro a comparar.
   * @returns Negativo si este filtro va antes.
   */
  compareOrder(other: FilterItemDirective): number {
    const self: Node = this.viewContainer.element.nativeElement;
    const position = self.compareDocumentPosition(other.viewContainer.element.nativeElement);
    return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  }

  /**
   * Destruye la vista de la barra principal, si existe.
   * @returns void
   */
  private destroyPinned(): void {
    if (!this.pinnedView) return;
    this.pinnedView.destroy();
    this.pinnedView = null;
  }
}
